import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, CheckCircle2, ArrowRight } from 'lucide-react';
import { getServices, getSettings } from '../lib/api';
import { useLanguage } from '../lib/LanguageContext';

const LOGO_URL = "/logo.png";
const HERO_IMAGE = "/hero-img.jpg";

// Icon mapping for services
const iconMap = {
  'bi-building': '🏗️',
  'bi-bricks': '🧱',
  'bi-paint-bucket': '🎨',
  'bi-grid-3x3': '🔲',
  'bi-droplet': '🏠', 
  'bi-trash3': '🗑️',
  'bi-hammer': '🔨',
  'bi-tools': '🔧',
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const revealRefs = useRef([]);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, settingsRes] = await Promise.all([
          getServices(),
          getSettings()
        ]);
        setServices(servicesRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [services]);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  // Get translated why us points
  const whyUsPoints = t('whyUs.points');

  // Helper function to get localized service field
  const getLocalizedField = (service, field) => {
    const enField = `${field}_en`;
    const deField = `${field}_de`;
    if (language === 'en' && service[enField]) {
      return service[enField];
    }
    return service[deField] || service[field] || '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Construction" 
            className="w-full h-full object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-2xl">
            <span 
              className="inline-block text-[#D4B982] text-sm font-medium uppercase tracking-widest mb-6 animate-fade-in"
            >
              {t('hero.subtitle')}
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in stagger-1"
              style={{ lineHeight: '1.1' }}
            >
              {t('hero.title1')}{' '}
              <span className="text-[#D4B982]">{t('hero.title2')}</span>{' '}
              {t('hero.title3')}
            </h1>
            <p 
              className="text-lg text-white/80 mb-8 max-w-lg animate-fade-in stagger-2"
            >
              {t('hero.description')}
            </p>
            <div 
              className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3"
            >
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 bg-[#D4B982] text-[#0A0A0A] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#E8D5A8] transition-colors btn-ripple"
                data-testid="hero-cta-angebot"
              >
                {t('hero.cta1')}
                <ChevronRight size={18} />
              </Link>
              <a
                href={`tel:${settings.phone || '0176 34681594'}`}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-white/10 transition-colors"
                data-testid="hero-cta-call"
              >
                <Phone size={18} />
                {t('hero.cta2')}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-[#D4B982] rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 lg:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div ref={addToRefs} className="reveal text-center mb-16">
            <span className="text-[#D4B982] text-sm font-medium uppercase tracking-widest">{t('services.subtitle')}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] mt-4">
              {t('services.title')}
            </h2>
            <p className="text-[#71717A] mt-4 max-w-2xl mx-auto">
              {t('services.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => (
              <div
                key={service.id}
                ref={addToRefs}
                className={`reveal stagger-${index + 1}`}
              >
                <Link
                  to={`/leistungen/${service.slug}`}
                  className="group block bg-white border border-[#E4E4E7] p-8 hover:border-[#D4B982] transition-all duration-500 card-hover h-full"
                  data-testid={`service-card-${service.slug}`}
                >
                  {service.image_url && (
                    <div className="relative h-48 mb-6 overflow-hidden -mx-8 -mt-8">
                      <img 
                        src={service.image_url} 
                        alt={getLocalizedField(service, 'title')}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{iconMap[service.icon] || '🔧'}</span>
                    <h3 className="text-xl font-semibold text-[#0A0A0A] group-hover:text-[#D4B982] transition-colors">
                      {getLocalizedField(service, 'title')}
                    </h3>
                  </div>
                  <p className="text-[#71717A] mb-4 line-clamp-2">
                    {getLocalizedField(service, 'short')}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-[#D4B982] group-hover:gap-3 transition-all">
                    {t('services.learnMore')}
                    <ArrowRight size={16} />
                  </span>
                </Link>
              </div>
            ))}
          </div>

          {services.length > 6 && (
            <div ref={addToRefs} className="reveal text-center mt-12">
              <Link
                to="/leistungen"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#262626] transition-colors"
                data-testid="view-all-services"
              >
                {t('services.viewAll')}
                <ChevronRight size={18} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div ref={addToRefs} className="reveal">
              <span className="text-[#D4B982] text-sm font-medium uppercase tracking-widest">{t('whyUs.subtitle')}</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] mt-4 mb-6">
                {t('whyUs.title1')} <span className="text-[#D4B982]">{t('whyUs.title2')}</span> {t('whyUs.title3')}
              </h2>
              <p className="text-[#71717A] text-lg mb-8">
                {t('whyUs.description')}
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {Array.isArray(whyUsPoints) && whyUsPoints.map((point, index) => (
                  <div 
                    key={point.title}
                    ref={addToRefs}
                    className={`reveal stagger-${index + 1} flex items-start gap-3`}
                  >
                    <CheckCircle2 className="text-[#D4B982] mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-[#0A0A0A]">{point.title}</h4>
                      <p className="text-sm text-[#71717A]">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div ref={addToRefs} className="reveal">
              <div className="relative">
                <img 
                  src="/construction-why-us.webp"
                  alt="Team at work"
                  className="w-full h-auto object-cover shadow-xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#D4B982] text-[#0A0A0A] p-6 shadow-lg">
                  <p className="text-4xl font-bold">10+</p>
                  <p className="text-sm font-medium uppercase tracking-wide">{t('whyUs.yearsExperience')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div ref={addToRefs} className="reveal">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('cta.title1')} <span className="text-[#D4B982]">{t('cta.title2')}</span>?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              {t('cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 bg-[#D4B982] text-[#0A0A0A] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#E8D5A8] transition-colors btn-ripple"
                data-testid="cta-contact"
              >
                {t('cta.button')}
                <ChevronRight size={18} />
              </Link>
              <a
                href={`tel:${settings.phone || '0176 34681594'}`}
                className="inline-flex items-center justify-center gap-2 border-2 border-[#D4B982] text-[#D4B982] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#D4B982] hover:text-[#0A0A0A] transition-colors"
                data-testid="cta-phone"
              >
                <Phone size={18} />
                {settings.phone || '0176 34681594'}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
